import React from 'react';
import RenderImage from '../utils/RenderImage';
import formatString from '../../utils/formatString';
import isObjectId from '../../utils/isObjectId';

const isYesNo = (v) => typeof v === 'string' && /^(s[ií]|no|yes)$/i.test(v);
const isPositive = (v) => (typeof v === 'boolean' ? v : /^(s[ií]|yes)$/i.test(String(v)));

const classifyValue = (value) => {
	if (isObjectId(value)) return 'photo';
	if (value && typeof value === 'object' && !Array.isArray(value) && Array.isArray(value.photos)) return 'section';
	return 'field';
};

const REPEAT_RE = /^(.+)-(\d+)$/;

const flattenEntries = (content) => {
	const list = [];

	(Array.isArray(content) ? content : []).forEach((entry) => {
		if (!entry || typeof entry !== 'object') return;
		Object.entries(entry).forEach(([key, value]) => {
			const match = key.match(REPEAT_RE);
			const base = match ? match[1] : key;
			const repeatIndex = match ? parseInt(match[2], 10) : null;
			list.push({ key, base, repeatIndex, value, kind: classifyValue(value) });
		});
	});

	const baseCounts = {};
	list.forEach((item) => {
		if (item.repeatIndex !== null) {
			baseCounts[item.base] = (baseCounts[item.base] || 0) + 1;
		}
	});

	return list.map((item) => ({
		...item,
		showIndex: item.repeatIndex !== null && baseCounts[item.base] > 1
	}));
};

const AnswerField = ({ value }) => {
	if (typeof value === 'boolean' || isYesNo(value)) {
		const positive = isPositive(value);
		const negative = !positive;
		return (
			<div className={`pn-qa-pill ${positive ? 'pos' : negative ? 'neg' : ''}`}>
				<svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
					{positive
						? <path d='M5 12l5 5L20 7' />
						: <path d='M6 6l12 12M18 6 6 18' />
					}
				</svg>
				{typeof value === 'boolean' ? (value ? 'Sí' : 'No') : String(value)}
			</div>
		);
	}
	if (typeof value === 'number') {
		return <div className='pn-qa-num'>{value.toLocaleString('en-US', { maximumFractionDigits: 2 })}</div>;
	}
	if (typeof value === 'string' && value.length > 60 && !value.includes('\n')) {
		return <div className='pn-qa-long'>{value}</div>;
	}
	const parts = String(value).split('\n');
	if (parts.length > 1) {
		return <div className='pn-qa-multi'>{parts.map((p, i) => <div className='pn-qa-pill' key={i}>{p}</div>)}</div>;
	}
	return <div className='pn-qa-pill'>{String(value)}</div>;
};

const AnswerPhoto = ({ imageId, label }) => (
	<div className='pn-qa-photo'>
		<RenderImage
			imageId={imageId}
			defaultSrc={`${process.env.PUBLIC_URL}/assets/ref-image2.svg`}
			alt={`${label} image`}
			refImage='payload'
		/>
	</div>
);

const AnswerSection = ({ value, label }) => {
	const photos = Array.isArray(value?.photos) ? value.photos : [];
	const items = Array.isArray(value?.items) ? value.items : [];

	const flatItems = [];
	items.forEach((it) => {
		if (it && typeof it === 'object') {
			Object.entries(it).forEach(([k, v]) => flatItems.push({ k, v }));
		}
	});

	const groupedByType = {};
	photos.forEach((p) => {
		const type = p?.photo_type ?? '—';
		(groupedByType[type] = groupedByType[type] || []).push(p);
	});
	const types = Object.keys(groupedByType);

	return (
		<div className='pn-qa-section'>
			{flatItems.length > 0 && (
				<div className='pn-qa-section-itemlist'>
					{flatItems.map(({ k, v }, i) => (
						<div className='pn-qa-section-item' key={i}>
							<span className='pn-qa-section-item-label'>{formatString(k)}:</span>
							<span className='pn-qa-section-item-value'>{String(v)}</span>
						</div>
					))}
				</div>
			)}

			{types.map((type) => (
				<div className='pn-qa-section-group' key={type}>
					{type !== '—' && (
						<div className='pn-qa-section-group-head'>
							<span className='pn-qa-section-group-dot' />
							<span className='pn-qa-section-group-label'>{type}</span>
						</div>
					)}
					<div className='pn-qa-section-photos'>
						{groupedByType[type].map((p, i) => (
							<RenderImage
								key={i}
								imageId={p?.image}
								defaultSrc={`${process.env.PUBLIC_URL}/assets/ref-image2.svg`}
								alt={`${label} ${type} photo`}
								refImage='payload'
							/>
						))}
					</div>
				</div>
			))}
		</div>
	);
};

const PayloadContentQA = ({ content }) => {
	const entries = flattenEntries(content);

	if (entries.length === 0) {
		return <p style={{ color: 'var(--ds-muted)', fontSize: 13 }}>No content available.</p>;
	}

	return (
		<div className='pn-content-wrap'>
			{entries.map((e, i) => (
				<div className='pn-qa-row' key={i}>
					<div className='pn-qa-number'>{String(i + 1).padStart(2, '0')}.</div>
					<div className='pn-qa-body'>
						<div className='pn-qa-label'>
							{formatString(e.base)}
							{e.showIndex && ` #${e.repeatIndex + 1}`}
						</div>
						{e.kind === 'photo' && <AnswerPhoto imageId={e.value} label={e.base} />}
						{e.kind === 'section' && <AnswerSection value={e.value} label={e.base} />}
						{e.kind === 'field' && <AnswerField value={e.value} />}
					</div>
				</div>
			))}
		</div>
	);
};

export default PayloadContentQA;
